import cx from 'classnames2';
import React, { MouseEvent } from 'react';

import { Checkbox } from '../../Checkbox';
import { Icon, IconSVGs } from '../../Icon';
import { useListTableContext } from '../ListTable';

import styles from './ListTable.Row.module.scss';

export interface IListTableRowProps {
  className?: string;
  href?: string;
  dataTest?: string;
  selected?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  checkboxAriaLabel?: string;
  value?: string;
  icon?: keyof typeof IconSVGs;
  iconClassName?: string;
  iconDataTest?: string;
  onClick?(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void;
  onChange?(value: string): any;
}

export const ListTableRow: React.FC<IListTableRowProps> = ({
  className,
  ariaLabel,
  checkboxAriaLabel,
  dataTest,
  children,
  onClick,
  onChange,
  selected = false,
  disabled,
  value,
  icon,
  iconClassName,
  iconDataTest,
  href
}) => {
  const listTableContext = useListTableContext();
  const Element = href ? 'a' : 'div';
  const onKeyDown = (e) => {
    if (!href && onClick) {
      switch (e.key) {
        case 'Enter':
          onClick(e);
          break;
        default:
          return;
      }
    }
  };

  let firstCell;

  if (listTableContext.isSelectable && listTableContext) {
    firstCell = (
      <div className={styles.checkbox}>
        <Checkbox.Small
          className={styles.checkboxLabel}
          onChange={() => {
            onChange(value);
          }}
          checked={selected}
          disabled={disabled}
          ariaLabel={checkboxAriaLabel}
        />
      </div>
    );
  } else if (listTableContext.hasIcon) {
    if (icon) {
      firstCell = (
        <div className={styles.icon}>
          <Icon icon={icon} className={iconClassName} data-test={iconDataTest} />
        </div>
      );
    } else {
      firstCell = <div className={styles.emptyCell} />;
    }
  }

  return (
    <Element
      tabIndex={0}
      className={cx(styles.row, className, {
        [styles.stateSelected]: selected,
        [styles.variantCondensed]: listTableContext.variant === 'condensed',
        [styles.variantMinimal]: listTableContext.variant === 'minimal',
        [styles.offsetIcon]: icon && listTableContext.hasIcon,
        [styles.offsetCheckbox]: listTableContext.isSelectable
      })}
      href={href ? href : null}
      onClick={(e) => onClick && onClick(e)}
      onKeyDown={onKeyDown}
      data-test={dataTest}
      aria-label={ariaLabel}
    >
      {firstCell}
      {children}
    </Element>
  );
};
